import i18n from '../../../i18n';
import {useTranslation} from 'react-i18next';
import MentionText from "../../Shared/MentionText";
import React, {useEffect, useState} from 'react';
import queryString from "query-string";
import {Dropdown} from 'react-bootstrap';
import AppBreadcrumb from "../../Shared/AppBreadcrumb";
import AppPagination from "../../Shared/AppPagination";
import {ShimmerTableRows} from "../../Shared/Shimmer";
import KpiCard from "../../Shared/KpiCard";
import httpService from "../../../services/http.service";
import ReportDrawer from "./ReportDrawer";
import "./report.css";
import {decideReport, formatReportDate, MediaHint, ReasonBadges, StateBadge, TypeBadge, UserCell} from "./reportUi";

const FILTER_KEYS = ['state', 'type', 'reason', 'from', 'to', 'q', 'page'];

/** Carte d'indicateur cliquable (filtre la liste sur ce statut). */
const StatCard = ({label, value, icon, tone, hint, active, loading, onClick}) => (
    <div className="col-lg-3 col-md-6 col-sm-6">
        <div role="button" tabIndex={0} onClick={onClick} className={`d-flex w-100 ${active ? 'text-primary' : ''}`} onKeyDown={(e) => e.key === 'Enter' && onClick()}
             style={{cursor: 'pointer', borderRadius: 12, boxShadow: active ? '0 0 0 2px currentColor' : 'none'}}>
            <KpiCard icon={icon} tone={tone} label={label} value={value ?? '–'} hint={hint} loading={loading} />
        </div>
    </div>
);

/**
 * Signalements (/reports) : tableau de bord (compteurs, contenus les plus signalés), filtres dans l'URL
 * (statut, type, motif, période, recherche ; jamais d'identifiant), tableau paginé avec menu d'actions,
 * fiche en panneau latéral (contenu, auteur, historique des signalements du même contenu, décision).
 */
const ReportList = ({history, location}) => {
    const {t} = useTranslation();
    const query = queryString.parse(location.search);
    const state = query.state === undefined ? 'pending' : query.state; // file « à traiter » par défaut

    const [stats, setStats] = useState(null);
    const [page, setPage] = useState(null);
    const [fetching, setFetching] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState(query.q || '');
    const [selected, setSelected] = useState(null); // signalement ouvert dans le panneau (pas dans l'URL)
    const [busyId, setBusyId] = useState(null);

    const setFilters = (changes) => {
        const next = {...query, state, ...changes};
        if (!('page' in changes)) delete next.page;
        Object.keys(next).forEach(key => (next[key] === '' || next[key] === undefined || next[key] === null) && delete next[key]);
        history.push(`/reports?${queryString.stringify(next)}`);
    };

    const loadStats = async () => {
        try {
            const {data} = await httpService.get('/reports/stats');
            setStats(data?.data || null);
        } catch (e) {
            setStats(null);
        }
    };

    const loadPage = async () => {
        setFetching(true);
        setError(null);
        try {
            const params = {state: state || undefined};
            FILTER_KEYS.forEach(key => key !== 'state' && query[key] && (params[key] = query[key]));
            const {data} = await httpService.get('/reports', {params});
            setPage(data);
        } catch (e) {
            setError(e?.response?.data?.message || t("Erreur de chargement."));
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => { loadStats(); }, []);
    useEffect(() => {
        setSearch(query.q || '');
        loadPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const refreshAfterDecision = (updated) => {
        setPage(p => p && ({...p, data: p.data.map(r => r.report_id === updated.report_id ? {...r, ...updated} : r)}));
        loadStats();
        window.dispatchEvent(new Event('reports:changed')); // badge du menu
    };

    const decide = async (report, decision) => {
        setBusyId(report.report_id);
        const updated = await decideReport(report, decision);
        setBusyId(null);
        if (updated) {
            refreshAfterDecision(updated);
            if (selected && selected.report_id === updated.report_id) setSelected(s => ({...s, ...updated}));
        }
    };

    const onPaginate = (direction) => {
        const current = parseInt(page?.current_page || 1, 10);
        setFilters({page: direction === 'previous' ? current - 1 : current + 1});
    };

    const reports = page?.data || [];
    const states = stats?.states || {};

    return (
        <div className="container-fluid">
            <AppBreadcrumb title={t("Signalements")} />

            {/* Tableau de bord */}
            <div className="row clearfix equal-height-row">
                <StatCard label={t("En attente")} value={states.pending} icon="fa-hourglass-half" tone="orange" loading={!stats}
                          hint={t("À traiter")} active={state === 'pending'} onClick={() => setFilters({state: 'pending'})} />
                <StatCard label={t("Validés")} value={states.approved} icon="fa-check" tone="green" loading={!stats}
                          hint={t("Signalements confirmés")} active={state === 'approved'} onClick={() => setFilters({state: 'approved'})} />
                <StatCard label={t("Rejetés")} value={states.rejected} icon="fa-times" tone="purple" loading={!stats}
                          hint={t("Signalements non fondés")} active={state === 'rejected'} onClick={() => setFilters({state: 'rejected'})} />
                <StatCard label={t("Reçus aujourd'hui")} value={stats?.today} icon="fa-flag" tone="blue" loading={!stats}
                          hint={t("Tous statuts : {{n}}", {n: stats?.total ?? 0})} active={state === ''} onClick={() => setFilters({state: ''})} />
            </div>

            {stats?.top_pending?.length > 0 && (
                <div className="card">
                    <div className="header"><h2>{t("Contenus les plus signalés (en attente)")}</h2></div>
                    <div className="body p-0">
                        <ul className="list-group list-group-flush">
                            {stats.top_pending.map(item => (
                                <li key={item.report_id} className="list-group-item d-flex align-items-center" style={{cursor: 'pointer'}} onClick={() => setSelected(item)}>
                                    <span className="badge badge-danger mr-3" style={{minWidth: 32}}>{item.same_content_count}</span>
                                    <TypeBadge type={item.type} />
                                    <span className="ml-2 text-truncate flex-grow-1">{(item.content?.text && <MentionText text={item.content.text} />) || (item.type === 'user' && item.content?.author ? `@${item.content.author.user_username}` : t("Contenu supprimé"))}</span>
                                    <small className="text-muted ml-2 text-nowrap">{item.content?.author ? `@${item.content.author.user_username}` : ''}</small>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* Filtres */}
            <div className="card">
                <div className="body">
                    <div className="row">
                        <div className="col-lg-4 col-md-12 mb-2">
                            <form onSubmit={(e) => { e.preventDefault(); setFilters({q: search.trim()}); }}>
                                <div className="input-group">
                                    <input className="form-control" value={search} onChange={(e) => setSearch(e.target.value)}
                                           placeholder={t("Rechercher : texte, auteur ou signaleur")} />
                                    <div className="input-group-append">
                                        <button className="btn btn-primary" type="submit"><i className="fa fa-search"></i></button>
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div className="col-lg-2 col-md-4 mb-2">
                            <select className="form-control" value={state} onChange={(e) => setFilters({state: e.target.value})}>
                                <option value="">{t("Tous les statuts")}</option>
                                <option value="pending">{t("En attente")}</option>
                                <option value="approved">{t("Validés")}</option>
                                <option value="rejected">{t("Rejetés")}</option>
                            </select>
                        </div>
                        <div className="col-lg-2 col-md-4 mb-2">
                            <select className="form-control" value={query.type || ''} onChange={(e) => setFilters({type: e.target.value})}>
                                <option value="">{t("Tous les contenus")}</option>
                                <option value="opinion">{t("Opinions")}</option>
                                <option value="comment">{t("Commentaires")}</option>
                                <option value="user">{t("Utilisateurs")}</option>
                            </select>
                        </div>
                        <div className="col-lg-2 col-md-4 mb-2">
                            <select className="form-control" value={query.reason || ''} onChange={(e) => setFilters({reason: e.target.value})}>
                                <option value="">{t("Tous les motifs")}</option>
                                {(stats?.reasons || []).map(reason => <option key={reason} value={reason}>{reason}</option>)}
                            </select>
                        </div>
                        <div className="col-lg-2 col-md-12 mb-2 d-flex">
                            <input type="date" className="form-control mr-1" title={t("Du")} value={query.from || ''} onChange={(e) => setFilters({from: e.target.value})} />
                            <input type="date" className="form-control" title={t("Au")} value={query.to || ''} onChange={(e) => setFilters({to: e.target.value})} />
                        </div>
                    </div>
                    {(query.q || query.type || query.reason || query.from || query.to) && (
                        <button type="button" className="btn btn-link btn-sm p-0" onClick={() => history.push(`/reports?${queryString.stringify({state})}`)}>
                            <i className="fa fa-times"></i>{' '}{t("Effacer les filtres")}
                        </button>
                    )}
                </div>
            </div>

            {/* Tableau */}
            <div className="card">
                <div className="body">
                    {error && <div className="alert alert-danger">{error}</div>}
                    <div className="table-responsive">
                        <table className="table table-hover mb-0 reports-table">
                            <thead>
                            <tr>
                                <th>{t("Date")}</th>
                                <th>{t("Contenu signalé")}</th>
                                <th className="text-center pl-3">{t("Signalements")}</th>
                                <th style={{width: 170}}>{t("Motifs")}</th>
                                <th>{t("Auteur")}</th>
                                <th>{t("Signalé par")}</th>
                                <th></th>
                            </tr>
                            </thead>
                            <tbody>
                            {fetching ? (
                                <ShimmerTableRows rows={8} columns={7} />
                            ) : reports.length === 0 ? (
                                <tr><td colSpan={7} className="text-center text-muted py-4">{t("Aucun signalement ne correspond aux filtres.")}</td></tr>
                            ) : reports.map(report => (
                                <tr key={report.report_id} style={{cursor: 'pointer'}} onClick={() => setSelected(report)}>
                                    <td className="text-nowrap small"><div className="mb-1"><StateBadge state={report.report_state} /></div>{formatReportDate(report.report_date)}</td>
                                    <td style={{maxWidth: 360}}>
                                        <div className="d-flex align-items-center">
                                            <MediaHint media={report.content?.media} />
                                            <div className="text-truncate" style={{minWidth: 0}}>
                                                <div className="text-truncate">{(report.content?.text && <MentionText text={report.content.text} />) || <em className="text-muted">{report.type === 'user' && report.content ? t("Compte signalé") : report.content ? t("(sans texte)") : t("Contenu supprimé")}</em>}</div>
                                                <div className="mt-1"><TypeBadge type={report.type} /></div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="text-center pl-3">
                                        <span className={`badge ${report.same_content_count > 1 ? 'badge-danger' : 'badge-default'}`}>{report.same_content_count}</span>
                                    </td>
                                    <td style={{width: 170, maxWidth: 170}}><ReasonBadges reasons={report.reasons} max={2} /></td>
                                    <td><UserCell user={report.content?.author} /></td>
                                    <td><UserCell user={report.reporter} /></td>
                                    <td className="text-right" onClick={(e) => e.stopPropagation()}>
                                        <Dropdown alignRight>
                                            <Dropdown.Toggle variant="light" size="sm" id={`report-actions-${report.report_id}`} disabled={busyId === report.report_id}>
                                                <i className="fa fa-ellipsis-h"></i>
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu popperConfig={{strategy: 'fixed'}}>
                                                <Dropdown.Item as="button" type="button" onClick={() => setSelected(report)}>
                                                    <i className="fa fa-eye mr-2"></i>{t("Voir la fiche")}
                                                </Dropdown.Item>
                                                {report.report_state !== 'approved' && (
                                                    <Dropdown.Item as="button" type="button" onClick={() => decide(report, 'approve')}>
                                                        <i className="fa fa-check mr-2 text-success"></i>{t("Valider le signalement")}
                                                    </Dropdown.Item>
                                                )}
                                                {report.report_state !== 'rejected' && (
                                                    <Dropdown.Item as="button" type="button" onClick={() => decide(report, 'reject')}>
                                                        <i className="fa fa-times mr-2 text-danger"></i>{t("Rejeter le signalement")}
                                                    </Dropdown.Item>
                                                )}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                    {page && page.last_page > 1 && (
                        <div className="mt-3">
                            <AppPagination
                                onNavigate={onPaginate}
                                currentPage={page.current_page}
                                lastPage={page.last_page}
                                totalItems={page.total}
                                hasNext={!!page.next_page_url}
                                hasPrevious={!!page.prev_page_url}
                            />
                        </div>
                    )}
                    {page && <div className="text-muted small mt-2">{i18n.t("{{n}} signalement(s)", {n: page.total})}</div>}
                </div>
            </div>

            {selected && (
                <ReportDrawer
                    report={selected}
                    onClose={() => setSelected(null)}
                    onDecided={(updated) => { refreshAfterDecision(updated); setSelected(s => ({...s, ...updated})); }}
                />
            )}
        </div>
    );
};

export default ReportList;
